import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  BookOpen, 
  ChevronRight, 
  AlertTriangle, 
  Wrench,
  Phone,
  Mail,
  ArrowRight
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import TopBar from "../components/TopBar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { buildBreadcrumb, SITE_URL } from "../lib/seoData";
import { getAllDefects } from "../data/troubleshootingData";

const TroubleshootingGuide = () => {
  const defects = getAllDefects();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const guideJsonLd = [
    buildBreadcrumb([
      { name: "Home", path: "/" },
      { name: "Gravure Troubleshooting Guide", path: "/gravure-troubleshooting" },
    ]),
    {
      "@type": "ItemList",
      name: "Gravure Printing Defects",
      itemListElement: defects.map((d, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: d.defectName,
        url: `${SITE_URL}/gravure-troubleshooting/${d.slug}`,
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Gravure Printing Troubleshooting Guide – Causes & Solutions | Crimson Inks"
        description="Expert gravure printing troubleshooting guide by Crimson Inks. Identify causes and proven ink & press solutions for 11 common gravure printing defects."
        path="/gravure-troubleshooting"
        keywords="gravure printing troubleshooting guide, gravure printing defects, doctor blade streaking, gravure ink problems, print defect solutions"
        jsonLd={guideJsonLd}
      />
      <TopBar />
      
      {/* Header */}
      <header className="fixed top-10 left-0 right-0 z-40 bg-white shadow-lg py-3">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/crimson-logo-with-name.png"
                alt="Crimson Inks"
                className="h-14 w-auto object-contain"
              />
            </Link>
            <nav className="hidden lg:flex items-center space-x-6">
              <Link to="/" className="text-gray-700 hover:text-[#8B1538] font-medium transition-colors">
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-[#8B1538] font-semibold">Technical Support</span>
            </nav>
            <Link to="/#contact">
              <Button className="bg-[#8B1538] hover:bg-[#DC143C] text-white px-6 py-2 rounded-md font-semibold">
                Contact Us
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-36 pb-16 bg-gradient-to-br from-[#8B1538] via-[#6d1029] to-[#4a0b1c] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <BookOpen className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Technical Support Center</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Gravure Printing Troubleshooting Guide
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Expert Ink & Press Solutions by Crimson Inks. Quickly identify problems, understand root causes, and implement corrective actions with a strong focus on ink performance.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 p-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Why This Guide Matters</h2>
                <p className="text-gray-700 leading-relaxed">
                  Gravure printing demands precision. Even minor deviations in ink formulation, press settings, or substrate behavior can lead to visible print defects. This troubleshooting guide is designed to help printers quickly identify problems, understand root causes, and implement corrective actions with a strong focus on ink performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Defects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Common Gravure Printing Defects
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Select a defect below to view detailed causes, solutions, and Crimson Inks recommendations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {defects.map((defect, index) => (
              <Link 
                key={defect.id} 
                to={`/gravure-troubleshooting/${defect.slug}`}
                className="group"
                data-testid={`defect-card-${defect.slug}`}
              >
                <Card className="h-full border-2 border-gray-100 hover:border-[#8B1538] transition-all duration-300 hover:shadow-xl group-hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 bg-[#8B1538]/10 rounded-lg flex items-center justify-center">
                        <Wrench className="h-5 w-5 text-[#8B1538]" />
                      </div>
                      <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        #{String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#8B1538] transition-colors">
                      {defect.defectName}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                      {defect.introDescription}
                    </p>
                    <div className="flex items-center text-[#8B1538] font-semibold text-sm">
                      View Solution
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#8B1538] to-[#DC143C]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Need Technical Assistance?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Our ink technologists are ready to help you solve complex printing challenges.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:+917706994400"
                className="inline-flex items-center bg-white text-[#8B1538] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <Phone className="h-5 w-5 mr-2" />
                +91 7706994400
              </a>
              <a 
                href="mailto:Crimson.inks7475@gmail.com"
                className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors"
              >
                <Mail className="h-5 w-5 mr-2" />
                Email Technical Support
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TroubleshootingGuide;
